import { useMemo } from "react";
import { useSelector } from "react-redux";
import { TimeRange } from "@shared/types";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { dashboardFilters } from "@shared/slices/filters.slice";
import {
  consolidatePortfolioChartData,
  formatTimeSeriesChartData,
} from "@shared/utils/charts";
import { sharedLib } from "@shared/services/shared.service";
import { DISPLAY_CURRENCIES } from "common/constants";
import {
  getAccountsTotals,
  withLivePoint,
} from "features/dashboard/common/utils";
import useDashboardAccounts from "features/dashboard/common/useDashboardAccounts.ts";

/**
 * The new-look dashboard's portfolio value and its history for a range: the
 * live total of the accounts in the dashboard's filter, and the amounts
 * history of the same accounts, ending on that live total. The history's
 * snapshots are hourly, so without its live end the chart's last point and
 * the range's change would disagree with the headline.
 */
const useDashboardHistory = (selectedTimeRange: TimeRange) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isUsd = displayCurrency === DISPLAY_CURRENCIES.USD;
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();

  const accountsData = useDashboardAccounts();
  const { isLoading: accountsLoading } =
    sharedLib.sharedApi.portfolio.endpoints.getPortfolioData.useQueryState(
      undefined
    );
  const { total: usdTotal } = getAccountsTotals(accountsData, true);
  const { total: btcTotal } = getAccountsTotals(accountsData, false);
  const total = isUsd ? usdTotal : btcTotal;
  const totalLoaded = !!accountsData.length;

  const { data: history, isSuccess: historyLoaded } =
    sharedLib.sharedApi.portfolio.endpoints.getAmountsHistory.useQueryState({
      time_range: selectedTimeRange,
    });
  const clientsFilter = useSelector(
    dashboardFilters.selectors.selectClientsFilter
  );
  const exchangeToClients = useSelector(
    dashboardFilters.selectors.selectExchangeToClientsMapping
  );
  const points = useMemo(() => {
    // Until the dashboard's account filter is set (it only loads with the
    // filter chips), every account counts, as with "all accounts" selected.
    const allAccounts: Record<string, string[]> = Object.fromEntries(
      Object.entries(history?.data || {}).map(([exchange, clients]) => [
        exchange,
        Object.keys(clients || {}),
      ])
    );
    const filtered = clientsFilter.length > 0;
    const consolidated = consolidatePortfolioChartData(
      history,
      selectedTimeRange,
      filtered ? clientsFilter : Object.values(allAccounts).flat(),
      filtered ? exchangeToClients : allAccounts
    );
    return formatTimeSeriesChartData(
      totalLoaded
        ? withLivePoint(consolidated, usdTotal, btcTotal)
        : consolidated,
      isUsd ? 1 : 2,
      false,
      null,
      precision
    ).filter((point) => point.rawX !== undefined);
  }, [
    history,
    selectedTimeRange,
    clientsFilter,
    exchangeToClients,
    isUsd,
    precision,
    usdTotal,
    btcTotal,
    totalLoaded,
  ]);

  const first = points[0]?.y;
  const last = points[points.length - 1]?.y;
  const change = first !== undefined && last !== undefined ? last - first : 0;
  const changePercent = first ? (change / first) * 100 : 0;

  return {
    total,
    totalLoading: accountsLoading && !totalLoaded,
    points,
    historyLoaded,
    change,
    changePercent,
    precision,
    isUsd,
    displayCurrency,
  };
};

export default useDashboardHistory;
